import { ReactNode, useState, useEffect, useRef } from "react";
import styles from "@/pages/index.module.css";
import { Step } from "@/typings/step";
import { Guess } from "@/typings/guess";
import { isSimilar } from "@/utils/scoring";

type PigeonSpeechProps = {
  step: Step;
  previousGuesses: Array<Guess>;
  onUpdateStep: (step: Step) => void;
};

function getSpeechText(step: Step, previousGuesses: Array<Guess>) {
  const latestGuess = previousGuesses[previousGuesses.length - 1];

  switch (step) {
    case Step.RoundOneGuessing:
    case Step.RoundTwoGuessing:
    case Step.RoundThreeGuessing: {
      const isPanicked = previousGuesses.length === 2 && !isSimilar(previousGuesses[1].score);
      return isPanicked ? ". . ." : "Hmm...";
    }

    case Step.RoundOneFail:
    case Step.RoundTwoFail:
    case Step.RoundThreeFail:
      return latestGuess.guess ? `"${latestGuess.guess}"?` : "???";

    case Step.Success: {
      return `"${latestGuess.guess}"!`;
    }
    default:
      return null;
  }
}

function getNextStepIfAvailable(step: Step) {
  switch (step) {
    case Step.RoundOneFail:
      return Step.RoundTwo;
    case Step.RoundTwoFail:
      return Step.RoundThree;
    default:
      return null;
  }
}

export function PigeonSpeech({
  step,
  previousGuesses,
  onUpdateStep,
}: PigeonSpeechProps): ReactNode {
  const [text, setText] = useState(getSpeechText(step, previousGuesses));
  const updateTextTimeout = useRef<NodeJS.Timeout | null>(null);

  const [nextStep, setNextStep] = useState<Step | null>(null);
  const updateNextStepTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setText(null);
    if (updateTextTimeout.current) {
      clearTimeout(updateTextTimeout.current);
    }
    updateTextTimeout.current = setTimeout(() => {
      setText(getSpeechText(step, previousGuesses));
    }, 50);
  }, [step, previousGuesses]);

  useEffect(() => {
    const newNextStep = getNextStepIfAvailable(step);
    if (updateNextStepTimeout.current) {
      clearTimeout(updateNextStepTimeout.current);
    }
    updateNextStepTimeout.current = setTimeout(() => {
      setNextStep(newNextStep);
    }, 750);
  }, [step]);

  return text === null ? null : (
    <div className={styles.pigeonSpeechContainer}>
      <div className={styles.pigeonSpeech}>{text}</div>
      {nextStep !== null && (
        <div className={styles.pigeonSpeechResponse}>
          <div className={styles.pigeonSpeechResponseFail}>✗</div>
          <button
            className={styles.pigeonSpeechResponseCta}
            onClick={() => {
              onUpdateStep(nextStep);
            }}
          >
            Next round ➜
          </button>
        </div>
      )}
    </div>
  );
}
